import { connect } from "react-redux";
import useQueryParams from "../../customHooks/useQueryParams";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import ButtonCustom from "../../components/Buttons/ButtomCustom";
import { Controller, useForm } from "react-hook-form";
import {
  cleanMessageSection,
  getSection,
  getSectionHistory,
  loadEmptySection,
  saveSection,
  disableLoadingSection,
  cleanIdSectionDuplicate,
  duplicateSection,
  getSectionPreview,
} from "../../store/actions";
import { useCallback, useEffect, useRef, useState } from "react";
import { setValuesHookForm } from "../../helpers/load_object_hook_form";
import { fieldsMessage } from "../../helpers/utils/fieldsMessage";
import { useNavigate } from "react-router-dom";
import DocumentBuider from "../../components/DocumentBuider/DocumentBuider";
import { documentBuilderVariants } from "../../enums/documentBuiderVariants";
import BusinessSelect from "../../components/BusinessSelect/BusinessSelect";
import RegionSelect from "../../components/RegionSelect/RegionSelect";
import LabelWithHelp from "../../components/LabelWithHelp/LabelWithHelp";
import CheckboxField from "../../components/CheckboxField/CheckboxField";
import stableStringify from "json-stable-stringify";
import { openHtmlAsPdfPreview } from "../../helpers/utils/openHtmlAsPdfPreview";
import { buildSnapshot } from "../../helpers/utils/buildSnapshot";
import useAlert from "../../customHooks/useAlert";
import { useContextActions } from "../../customHooks/useContextActions";
import ActionButtons from "../../components/ActionButtons/ActionButtons";

const SectionDetail = ({
  section,
  loadingSection,
  messageSection,
  isSectionSave,
  saveSectionRequest,
  duplicateSection,
  getSectionRequest,
  loadEmptySection,
  cleanMessageSection,
  getSectionHistoryRequest,
  disableLoadingSection,
  sectionIdDuplicate,
  cleanIdSectionDuplicate,
  errorLoadingSection,
	errorLoadingSectionHistory,
  errorSectionPreview,
  errorSectionDuplicate,
  getSectionPreviewRequest,
  sectionPreview,
}) => {
  const [content, setContent] = useState([]);
  const [contentError, setContentError] = useState(null);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      active: true,
    }
  });

  const initialHasRef = useRef(null);
  const saveActionRef = useRef(null);
  const titlePreviewRef = useRef("");

  const { confirmAlert, successAlert, errorAlert, okAlert } = useAlert();

  const navigate = useNavigate();
  const params = useQueryParams();
  const id = params.get("id");
  const idHistory = params.get("idHistory");
    
  function handleBack() {
    loadEmptySection();
    navigate("/sections");
  }
  async function handleSave(values) {
    saveActionRef.current = null;
    
    const isValidContent = validateContent();
    if (!isValidContent) {
      setContentError(true);
      return;
    }
    setContentError(false);

    const isEdit = id !== null;
    const baseBody = {
      title: values.title,
      description: values.description,
      active: values.active,
      idRegion: values.region?.value,
      idBusiness: values.business?.value,
      content,
    }
    const body = isEdit ? { id, ...baseBody } : baseBody;

    if(isEdit) {
      const result = await confirmAlert("Attention! All documents associated with this Section will be updated to Draft status. Do you want to continue?")
      
        if(!result.isConfirmed) return;
      
    }

    await saveSectionRequest({
      body,
      method: isEdit ? "PUT" : "POST",
    });
  }
  function handleDuplicate() {
    if (id === null) return;

    const isValidContent = validateContent();
    if (!isValidContent) {
      setContentError(true);
      return;
    }
    setContentError(false);

    const values = getValues();

    const currentSnapshot = buildSnapshot({
      values,
      content,
    });

    if (stableStringify(initialHasRef.current) === stableStringify(currentSnapshot)) {
      saveActionRef.current = "DUPLICATE_ONLY";
      duplicateSection({ sectionId: id });
      return;
    } else {
      confirmAlert(
        "You have unsaved changes. Do you want to save before duplicating?"
			).then((result) => {
        if (result.isConfirmed) {
          saveActionRef.current = "SAVE_AND_DUPLICATE";
					saveSectionRequest({
						body: {
							id,
							...currentSnapshot,
						},
						method: "PUT",
					});
				}
			});
    }
  }
  function validateContent() {
    return content.length > 0;
  }
  function handlePreview() {
      if (!id) return;

      const isValidContent = validateContent();
      if (!isValidContent) {
        setContentError(true);
        return;
      }
      setContentError(false);
      
      const values = getValues();
      titlePreviewRef.current = values.title ?? "Section Preview";
      
      const currentSnapshot = buildSnapshot({
        values,
        content,
      });
      if (stableStringify(initialHasRef.current) === stableStringify(currentSnapshot)) {
        saveActionRef.current = "PREVIEW_ONLY";
        getSectionPreviewRequest({ sectionId: id });
        return;
      } else {
        confirmAlert(
          "You have unsaved changes. Do you want to save before previewing?"
        ).then((result) => {
          if(result.isConfirmed) {
            saveActionRef.current = "SAVE_AND_PREVIEW";
            saveSectionRequest({
              body: {
                id: id,
                ...currentSnapshot
              },
              method: "PUT"
            });
          }
          return;
        })
      }
  }
  const actions = useContextActions({
    context: "section",
    screen: "detail",
  })
  const editableWithSave = actions.some(e => e === "save");

  const handleBuilderChange = useCallback((data) => {
    setContent(data.content);
  }, []);

  useEffect(() => {
    if (id !== null) {
      getSectionRequest({ sectionId: id });
    } else if (idHistory !== null) {
      getSectionHistoryRequest({ idHistory });
    } else {
      disableLoadingSection();
    }

    return () => {
      loadEmptySection();
      cleanMessageSection();
      cleanIdSectionDuplicate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    if (isSectionSave !== null) {
      if(isSectionSave) {
        if(saveActionRef.current === "SAVE_AND_PREVIEW") {
          okAlert(
            'Section saved successfully!'
          )
          .then((result) => {
            if(result.isConfirmed) {
              cleanMessageSection();
              getSectionPreviewRequest({ sectionId: id });
            }
            if (result.isDismissed) {
              cleanMessageSection();
            }
            saveActionRef.current = null;
            initialHasRef.current = buildSnapshot({
              values: getValues(),
              content,
            });
          });
          return;
        }
        successAlert(
          `Section ${id === null ? "created" : "updated"} successfully!`,
          {
            didClose: () => {
              if(saveActionRef.current === "SAVE_AND_PREVIEW") {
                cleanMessageSection();
                getSectionPreviewRequest({ sectionId: id });
                initialHasRef.current = buildSnapshot({
                  values: getValues(),
                  content,
                });
                saveActionRef.current = null;
              } else if(saveActionRef.current === "SAVE_AND_DUPLICATE") {
                cleanMessageSection();
                duplicateSection({ sectionId: id });
                initialHasRef.current = buildSnapshot({
                  values: getValues(),
                  content,
                });
                saveActionRef.current = null;
              } else {
                cleanMessageSection();
                loadEmptySection();
                navigate("/sections");
              }
            }
          }
        );
      } else {
        errorAlert(
          messageSection ?? "Something wrong, try again later.",
          {
            didClose: () => {
              cleanMessageSection();
            }
          }
        );
      }
    }
    // eslint-disable-next-line
  }, [isSectionSave]);
	useEffect(() => {
		if (errorLoadingSection !== null) {
      errorAlert(
        messageSection ?? "Something wrong, try again later.",
        {
          didClose: () => {
            cleanMessageSection();
            loadEmptySection();
            navigate("/sections");
          }
        }
      )
		}
		// eslint-disable-next-line
	}, [errorLoadingSection]);
	useEffect(() => {
		if (errorLoadingSectionHistory !== null) {
      errorAlert(
        messageSection ?? "Something wrong, try again later.",
        {
          didClose: () => {
            cleanMessageSection();
            loadEmptySection();
            navigate("/sections");
          }
        }
      )
		}
  	// eslint-disable-next-line
	}, [errorLoadingSectionHistory]);
	useEffect(() => {
    if(sectionIdDuplicate !== null) {
      if(sectionIdDuplicate) {
        successAlert(
          "Section duplicated successfully!",
          {
            text: "You will be redirected to the copied section!",
            timer: undefined,
						showConfirmButton: true,
						customClass: {
							confirmButton: "btn btn-braskemPrimary btn-lg px-5",
						},
            didClose: () => {
              cleanMessageSection();
              saveActionRef.current = null;
              navigate("/section?id=" + sectionIdDuplicate);
            }
          }
        );
        return;
      } 
      if(errorSectionDuplicate) {
        errorAlert(
          errorSectionDuplicate ? errorSectionDuplicate : "Something wrong, try again later.",
        );
      }
    }
		// eslint-disable-next-line
	}, [sectionIdDuplicate, errorSectionDuplicate]);
  useEffect(() => {
    if (!section) return;
    
    setValuesHookForm(section, setValue);
    setValue("region", {
      value: section.idRegion,
    });
    setValue("business", {
      value: section.idBusiness,
    });
    setValue("active", section.active ?? true);

    let normalizeContent = [];

    if (Array.isArray(section.content)) {
			normalizeContent = section.content.map(block => ({
				idSnippet: String(block.idSnippet),
        order: block.order, 
			}));
			
			setContent(normalizeContent);
		}

		initialHasRef.current = buildSnapshot({
			values: {
				title: section.title,
				description: section.description,
				region: { value: section.idRegion },
    		business: { value: section.idBusiness },
        active: section.active,
			},
			content: normalizeContent,
		});
    // eslint-disable-next-line
  }, [section]);
  useEffect(() => {
      if (sectionPreview) {
        openHtmlAsPdfPreview({
          html: sectionPreview.html,
          title: titlePreviewRef.current || "Section Preview",
        });
        return;
      }
      if(errorSectionPreview) {
        saveActionRef.current = null;
        errorAlert(
          errorSectionPreview ? errorSectionPreview : "Try again later!",
          {
            didClose: () => {
              cleanMessageSection();
            },
          }
        );
      }
  
      return () => {
        saveActionRef.current = null;
      }
      // eslint-disable-next-line
  }, [sectionPreview, errorSectionPreview]);
  useEffect(() => {
    if (contentError) {
      const isValid = validateContent();
      if (isValid) {
        setContentError(false);
      }
    }
    // eslint-disable-next-line
  }, [content]);

  return (
    <div className="page-content">
      <Helmet>
        <title>Section</title>
      </Helmet>

      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Row className="my-2">
                  <>
                    <form onSubmit={handleSubmit(handleSave)}>
                      {loadingSection ? (
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{ height: "400px", width: "100%" }}
                        >
                          <Spinner
                            color="primary"
                            style={{ height: "5rem", width: "5rem" }}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between my-2">
                            <h2>
                              {id === null
                                ? idHistory === null
                                  ? "CREATE"
                                  : "VIEW"
                                : "EDIT"}{" "}
                              SECTION
                            </h2>

                            <div className="d-flex gap-2 mb-4">
                              <ButtonCustom
                                type="cancel"
                                onClick={() => handleBack()}
                              />
                              <ActionButtons 
                                actions={actions}
                                handlers={{
                                  preview: handleSubmit(handlePreview),
                                  save: handleSubmit(handleSave),
                                  duplicate: handleSubmit(handleDuplicate),
                                }}
                                context="section"
                              />
                            </div>
                          </div>

                          <Row>
                            <Col md="6">
                              <FormGroup className="mb-3">
                                <Label
                                  htmlFor="title"
                                  className="label-detail-form"
                                >
                                  Title
                                </Label>
                                <input
                                  className="form-control"
                                  id="title"
                                  type="text"
                                  disabled={(idHistory !== null) || (!editableWithSave)}
                                  style={(idHistory !== null) || (!editableWithSave) ? {color: "#00000060"} : {}}
                                  placeholder="Section title"
                                  {...register("title", {
                                    required:
                                      fieldsMessage.required("Section title"),
                                    validate: (value) =>
                                      value.trim() !== "" ||
                                      fieldsMessage.required("Section title"),
                                    maxLength: {
                                      value: 100,
                                      message: fieldsMessage.maxLength(
                                        "Section title",
                                        100,
                                      ),
                                    },
                                  })}
                                />
                                <p className="error-form">
                                  {errors.title?.message}
                                </p>
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <Label className="label-detail-form">Created By</Label>
                              <input 
                                className="form-control"
                                id="createUser"
                                {...register("createUser")}
                                disabled
                                style={{ color: '#00000060'}}
                              />
                            </Col>
                            <Col md="3">
                              <FormGroup className="mb-3">
                                <Label className="label-detail-form">
                                  Creation Date
                                </Label>
                                <input
                                  className="form-control"
                                  {...register("createDate")}
                                  disabled
                                  style={{ color: '#00000060'}}
                                />
                              </FormGroup>
                            </Col>
                            
                          </Row>
                          <Row>
                            <Col md="3">
                              <FormGroup className="mb-3">
                                <Controller
                                  name="region"
                                  control={control}
                                  render={({ field }) => (
                                    <RegionSelect
                                      value={field.value}
                                      onChange={field.onChange}
                                      editable={idHistory || !editableWithSave}
                                    />
                                  )}
                                  rules={{
                                    required: fieldsMessage.required("Region"),
                                  }}
                                />
                                <p className="error-form">
                                  {errors.region?.message}
                                </p>
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup className="mb-3">
                                <Controller
                                  name="business"
                                  control={control}
                                  render={({ field }) => (
                                    <BusinessSelect
                                      value={field.value}
                                      onChange={field.onChange}
                                      editable={idHistory || !editableWithSave}
                                    />
                                  )}
                                  rules={{
                                    required:
                                      fieldsMessage.required("Business"),
                                  }}
                                />
                                <p className="error-form">
                                  {errors.business?.message}
                                </p>
                              </FormGroup>
                            </Col>
                            <Col md="3">
                                <Label className="label-detail-form">Updated By</Label>
                                <input 
                                  className="form-control"
                                  id="updateUser"
                                  {...register("updateUser")}
                                  disabled
                                  style={{ color: '#00000060'}}
                                />
                            </Col>
                            <Col md="3">
                              <FormGroup className="mb-3">
                                <Label className="label-detail-form">
                                  Update Date
                                </Label>
                                <input
                                  className="form-control"
                                  {...register("updateDate")}
                                  disabled
                                  style={{ color: '#00000060'}}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md="12">
                              <FormGroup className="mb-3">
                                <Label
                                  htmlFor="description"
                                  className="label-detail-form"
                                >
                                  Description
                                </Label>
                                <input
                                  className="form-control"
                                  id="description"
                                  type="text"
                                  disabled={(idHistory !== null) || (!editableWithSave)}
                                  style={(idHistory !== null) || (!editableWithSave) ? {color: "#00000060"} : {}}
                                  placeholder="Section description"
                                  {...register("description")}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          
                          <Row>
                            <Col md="12">
                              <hr style={{marginBottom: "30px"}}/>
                            </Col>
                          </Row>
                        </>
                      )}

                      <Row>
                        <Col md="12">
                          {!loadingSection && (
                            <Label className="label-detail-form">
                              Section Content{" "}
                              {!!contentError && (
                                <span className="error-form">
                                  Content is required.
                                </span>
                              )}
                            </Label>
                          )}
                          <DocumentBuider
                            variant={documentBuilderVariants.SECTION}
                            initialSelectedItems={section?.content || []}
                            handleChange={handleBuilderChange}
                            parentId={id}
                            loading={loadingSection}
                            disabled={(idHistory !== null) || (!editableWithSave)}
                            error={!!contentError}
                          />
                        </Col>
                      </Row>
                      
                      {!loadingSection && (
                        <Row className="mt-4">
                          <Col md="12">
                            <div className="d-flex align-items-center gap-2 mt-4">
                              <CheckboxField
                                id="active"
                                name="active"
                                register={register}
                                editable={(idHistory !== null ? true : false) || (!editableWithSave)}
                              />
                              <LabelWithHelp
                                htmlFor="active"
                                label="Active"
                                typeMessage="active"
                                entity="section"
                                className="mb-0"
                              />
                            </div>
                          </Col>
                          <div className="d-flex justify-content-end my-4 gap-2">
                            <ButtonCustom
                                type="cancel"
                                onClick={() => handleBack()}
                              />
                              <ActionButtons 
                                actions={actions.filter( (a) => a !== "duplicate")}
                                handlers={{
                                  preview: handleSubmit(handlePreview),
                                  save: handleSubmit(handleSave),
                                }}
                                context="section"
                              />
                          </div>
                        </Row>
                        
                      )}
                      
                    </form>
                  </>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStateToProps = ({
  SectionReducer: {
    section,
    loadingSection,
    messageSection,
    isSectionSave,
    sectionIdDuplicate,
    errorLoadingSection,
	  errorLoadingSectionHistory,
    errorSectionPreview,
    errorSectionDuplicate,
    sectionPreview,
  },
}) => ({
  section,
  loadingSection,
  messageSection,
  isSectionSave,
  sectionIdDuplicate,
  errorLoadingSection,
	errorLoadingSectionHistory,
  errorSectionPreview,
  errorSectionDuplicate,
  sectionPreview,
});

const mapDispatchToProps = {
  saveSectionRequest: saveSection.request,
  getSectionRequest: getSection.request,
  getSectionHistoryRequest: getSectionHistory.request,
  duplicateSection: duplicateSection.request,
  getSectionPreviewRequest: getSectionPreview.request,
  loadEmptySection,
  cleanMessageSection,
  disableLoadingSection,
  cleanIdSectionDuplicate,
};

export default connect(mapStateToProps, mapDispatchToProps)(SectionDetail);
